import React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Mail, Phone } from 'lucide-react';
import { classifyHref, isRouterPath } from '@/lib/links';
import { cn } from '@/lib/utils';

/**
 * SmartLink — automatische, WCAG-konforme Kennzeichnung externer Links.
 *
 * Klassifiziert das Ziel ueber `classifyHref` und rendert je nach Art:
 *  - intern + Router-Pfad -> <Link to> (kein Icon, kein Badge)
 *  - mailto / tel         -> <a href> + optionales Mail/Phone-Icon (aria-hidden)
 *  - extern               -> <a href target rel> + Extern-Icon + SR-Hinweis
 *  - sonst (interne abs. URL / /api / unknown) -> schlichtes <a href>
 *
 * Icons sind immer `aria-hidden`. Bei `target="_blank"` wird IMMER
 * `rel="noopener noreferrer"` gesetzt.
 */
export const SmartLink = React.forwardRef(
  ({ href, children, className, newTab = true, showIcon = true, iconClassName, ariaLabel, ...rest }, ref) => {
    const { kind } = classifyHref(href);

    // Intern + echter Router-Pfad -> react-router <Link>
    if (kind === 'internal' && isRouterPath(href)) {
      return (
        <Link ref={ref} to={href} className={className} aria-label={ariaLabel} {...rest}>
          {children}
        </Link>
      );
    }

    // mailto / tel -> einfacher <a>, optionales Icon, KEIN Extern-Badge, kein target/rel
    if (kind === 'mail' || kind === 'tel') {
      const Icon = kind === 'mail' ? Mail : Phone;
      return (
        <a ref={ref} href={href} className={className} aria-label={ariaLabel} {...rest}>
          {children}
          {showIcon && (
            <Icon aria-hidden="true" className={cn('inline-block h-3 w-3 opacity-70', iconClassName)} />
          )}
        </a>
      );
    }

    // Externer Link -> target/rel + Kennzeichnung
    if (kind === 'external') {
      const suffix = newTab ? ' (öffnet in neuem Tab)' : ' (externe Seite)';
      return (
        <a
          ref={ref}
          href={href}
          target={newTab ? '_blank' : undefined}
          rel={newTab ? 'noopener noreferrer' : 'noopener'}
          className={className}
          aria-label={ariaLabel ? `${ariaLabel}${suffix}` : undefined}
          {...rest}
        >
          {children}
          {showIcon && (
            <ExternalLink aria-hidden="true" className={cn('inline-block h-3 w-3 opacity-70', iconClassName)} />
          )}
          {showIcon && !ariaLabel && <span className="sr-only">{suffix}</span>}
        </a>
      );
    }

    // Interne absolute URL / /api/files / unknown -> schlichtes <a> ohne Badge
    return (
      <a ref={ref} href={href} className={className} aria-label={ariaLabel} {...rest}>
        {children}
      </a>
    );
  },
);
SmartLink.displayName = 'SmartLink';

/**
 * DownloadLink — echter Download-Link (ersetzt `window.open`).
 *
 * Immer ein <a target="_blank" rel="noopener noreferrer"> (tastatur- und
 * SR-tauglich). Haengt einen SR-Hinweis mit Dateityp an; bei externem Ziel
 * zusaetzlich das Extern-Icon.
 */
export const DownloadLink = React.forwardRef(
  ({ href, children, className, fileLabel = 'PDF', ariaLabel, ...rest }, ref) => {
    const { kind } = classifyHref(href);
    const external = kind === 'external';
    const suffix = ` (${fileLabel}, öffnet in neuem Tab)`;
    return (
      <a
        ref={ref}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        aria-label={ariaLabel ? `${ariaLabel}${suffix}` : undefined}
        {...rest}
      >
        {children}
        {external && <ExternalLink aria-hidden="true" className="inline-block h-3 w-3 opacity-70" />}
        {!ariaLabel && <span className="sr-only">{suffix}</span>}
      </a>
    );
  },
);
DownloadLink.displayName = 'DownloadLink';

export default SmartLink;
